import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Card, Form, Button, InputGroup } from 'react-bootstrap';
import axios from 'axios';
import { Navigate } from 'react-router-dom';

function Register() {
    const { register, handleSubmit, formState: { errors }, getValues } = useForm();
    const [error, setError] = useState('');
    const [isRegistered, setIsRegistered] = useState(false);
    const [countryCode, setCountryCode] = useState('+31');

    const countryOptions = [
        { code: '+31', name: 'Netherlands', flag: '🇳🇱' },
        { code: '+1', name: 'United States', flag: '🇺🇸' },
        { code: '+44', name: 'United Kingdom', flag: '🇬🇧' },
        { code: '+49', name: 'Germany', flag: '🇩🇪' },
        { code: '+33', name: 'France', flag: '🇫🇷' },
        { code: '+32', name: 'Belgium', flag: '🇧🇪' },
        { code: '+34', name: 'Spain', flag: '🇪🇸' },
        { code: '+39', name: 'Italy', flag: '🇮🇹' },
        { code: '+46', name: 'Sweden', flag: '🇸🇪' },
        { code: '+47', name: 'Norway', flag: '🇳🇴' },
        { code: '+45', name: 'Denmark', flag: '🇩🇰' },
    ];

    const validatePassword = (value) => {
        const hasUpperCase = /[A-Z]/.test(value);
        const hasLowerCase = /[a-z]/.test(value);
        const hasNumbers = /\d/.test(value);
        const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(value);
        const hasMinLength = value.length >= 8;

        if (!hasMinLength) return "Password must be at least 8 characters long";
        if (!hasUpperCase) return "Password must contain at least one uppercase letter";
        if (!hasLowerCase) return "Password must contain at least one lowercase letter";
        if (!hasNumbers) return "Password must contain at least one number";
        if (!hasSpecialChar) return "Password must contain at least one special character";
        return true;
    };

    const validateEmail = (value) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(value) || "Please enter a valid email address";
    };

    const validatePhone = (value) => {
        const phoneRegex = /^[0-9]{8,15}$/; // 8-15 digits
        return phoneRegex.test(value) || "Please enter a valid phone number (8-15 digits)";
    };

    const validateStudentNumber = (value) => {
        return !value.includes('%') || "Student number may not contain '%'";
    };

    function submit(e) {
        let bodyFormData = new FormData();
        bodyFormData.append("studentNumber", e.studentNumber);
        bodyFormData.append("firstName", e.firstName);
        bodyFormData.append("lastName", e.lastName);
        bodyFormData.append("year", e.year);
        bodyFormData.append("studyMode", e.studyMode === "true");
        bodyFormData.append("password", e.password);
        bodyFormData.append("email", e.email);
        bodyFormData.append("phone", countryCode + e.phoneNumber);

        axios.post("/auth/register", bodyFormData,{headers: {'content-type': 'application/x-www-form-urlencoded'}})
            .then(response => {
                if(response.status === 201) {
                    setIsRegistered(true);
                }else{
                    if(response.response && response.response.data && response.response.data.message){
                        setError(response.response.data.message);
                    }else{
                        setError("An unexpected error occurred.");
                    }
                }
            })
            .catch(err => {
                // Handle error responses from server here
                if(err.response && err.response.data && err.response.data.message){
                    setError(err.response.data.message);
                }else{
                    setError("An unexpected error occurred.");
                }
            });
    }

    if (isRegistered) {
        return <Navigate to="/login" />;
    }

    return (
        <>
            <Card.Title>Sign-up</Card.Title>
            <Form onSubmit={handleSubmit(submit)}>
                <label style={{color: "red"}}>{error}</label>
                <Form.Group className="mb-3" controlId="formStudentNumber">
                    <Form.Label>Student Number</Form.Label>
                    <Form.Control {...register("studentNumber", { required: true, validate: validateStudentNumber })} type="text" placeholder="Enter Student Number" />
                    {errors.studentNumber && errors.studentNumber.message && <p style={{ color: 'red' }}>{errors.studentNumber.message}</p>}
                </Form.Group>
                <Form.Group className="mb-3" controlId="formFirstName">
                    <Form.Label>First Name</Form.Label>
                    <Form.Control {...register("firstName", { required: true })} type="text" placeholder="Enter First Name" />
                </Form.Group>
                <Form.Group className="mb-3" controlId="formLastName">
                    <Form.Label>Last Name</Form.Label>
                    <Form.Control {...register("lastName", { required: true })} type="text" placeholder="Enter Last Name" />
                </Form.Group>
                <Form.Group className="mb-3" controlId="formEmail">
                    <Form.Label>Email Address</Form.Label>
                    <Form.Control {...register("email", { required: "Email is required!", validate: validateEmail })} type="email" placeholder="Enter Email Address" />
                    {errors.email && <p style={{ color: 'red' }}>{errors.email.message}</p>}
                </Form.Group>
                <Form.Group className="mb-3" controlId="formPhoneNumber">
                    <Form.Label>Phone Number</Form.Label>
                    <InputGroup>
                        <Form.Select
                            value={countryCode}
                            onChange={(e) => setCountryCode(e.target.value)}
                            style={{ maxWidth: '150px' }}
                        >
                            {countryOptions.map((country) => (
                                <option key={country.code} value={country.code}>
                                    {country.flag} {country.code}
                                </option>
                            ))}
                        </Form.Select>
                        <Form.Control {...register("phoneNumber", { required: "Phone number is required!", validate: validatePhone })} type="tel" placeholder="123456789" />
                    </InputGroup>
                    {errors.phoneNumber && <p style={{ color: 'red' }}>{errors.phoneNumber.message}</p>}
                </Form.Group>
                <Form.Group className="mb-3" controlId="formYear">
                    <Form.Label>Year</Form.Label>
                    <Form.Control {...register("year", { required: true })} type="number" placeholder="Enter Year" />
                </Form.Group>
                <Form.Group className="mb-3" controlId="formStudyMode">
                    <Form.Label>Study Mode</Form.Label>
                    <Form.Select {...register("studyMode", { required: true })}>
                        <option value="true">Full-time</option>
                        <option value="false">Part-time</option>
                    </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3" controlId="formPassword">
                    <Form.Label>Password</Form.Label>
                    <Form.Control {...register("password", { required: "Password is required!", validate: validatePassword })} type="password" placeholder="Password" />
                    {errors.password && <p style={{ color: 'red' }}>{errors.password.message}</p>}
                </Form.Group>
                <Form.Group className="mb-3" controlId="formPasswordRepeat">
                    <Form.Label>Repeat Password</Form.Label>
                    <Form.Control {...register("passwordRepeat", {
                        required: "Please confirm password!",
                        validate: value => value === getValues().password || "The passwords do not match"
                    })}
                                  type="password" placeholder="Repeat Password"
                    />
                    {errors.passwordRepeat && <p style={{ color: 'red' }}>{errors.passwordRepeat.message}</p>}
                </Form.Group>
                <Button variant="primary" style={{width: '100%'}} type="submit">
                    Register
                </Button>
            </Form>
        </>
    );
}

export default Register;
